<script setup lang="ts">
import type { Comment } from '~/types/blog'
import { resolveAvatarUrl } from '~/composables/useAvatar'
import { parseUserAgent } from '~/composables/useUserAgent'

defineOptions({ name: 'BlogCommentTree' })

const props = defineProps<{
  comments: Comment[]
  gravatarMirror?: string
  replyingId?: number
  replyingParentId?: number
  level?: number
  allowReply?: boolean
}>()
const emit = defineEmits<{ (e: 'reply', item: Comment): void }>()
const collapsedMap = reactive<Record<number, boolean>>({})

const authLabel = (item: Comment) => item.identity?.authTypeName || item.identity?.authType || ''

const formatTime = (value?: string) => {
  if (!value) {
    return '-'
  }
  return new Date(value).toLocaleString('zh-CN')
}

const toggleChildren = (id: number) => {
  collapsedMap[id] = !collapsedMap[id]
}

const isCollapsed = (id: number) => !!collapsedMap[id]
const avatarUrl = (item: Comment) => resolveAvatarUrl(item.emailHash, props.gravatarMirror)
const currentLevel = computed(() => Number(props.level || 0))
const isVirtualComment = (item: Comment) => item.localOnly === true || Number(item.ID || 0) <= 0
const canReply = (item: Comment) => {
  if (props.allowReply === false) {
    return false
  }
  return !isVirtualComment(item)
}
const parseUa = (item: Comment) => {
  const ua = parseUserAgent(item.agent)
  const system = ua.system || 'Unknown'
  const browser = ua.browser || 'Unknown'
  const unknownSystem = /^unknown$/i.test(system)
  const unknownBrowser = /^unknown$/i.test(browser)
  const show = !(unknownSystem && unknownBrowser)
  return {
    system: unknownSystem ? '未知' : system,
    browser: unknownBrowser ? '未知' : browser,
    show,
    systemIcon:
      /windows/i.test(system)
        ? 'fa-brands fa-windows'
        : /mac|os x|ios/i.test(system)
          ? 'fa-brands fa-apple'
          : /android/i.test(system)
            ? 'fa-brands fa-android'
            : /linux/i.test(system)
              ? 'fa-brands fa-linux'
              : 'fa-solid fa-desktop',
    browserIcon:
      /chrome|chromium/i.test(browser)
        ? 'fa-brands fa-chrome'
        : /safari/i.test(browser)
          ? 'fa-brands fa-safari'
          : /firefox/i.test(browser)
            ? 'fa-brands fa-firefox-browser'
            : /edge/i.test(browser)
              ? 'fa-brands fa-edge'
              : /opera/i.test(browser)
                ? 'fa-brands fa-opera'
                : 'fa-solid fa-globe'
  }
}

const canShowReplyForm = (item: Comment) => {
  if (!canReply(item)) {
    return false
  }
  if (props.replyingId !== item.ID) {
    return false
  }
  if (props.replyingParentId === 0) {
    return currentLevel.value === 0
  }
  if ((props.replyingParentId || 0) > 0) {
    return currentLevel.value === 1
  }
  return true
}

const resolveAuthorUrl = (value?: string) => {
  const text = String(value || '').trim()
  if (!text) {
    return ''
  }
  const raw = text.includes('://') ? text : `https://${text}`
  try {
    const parsed = new URL(raw)
    if (!/^https?:$/.test(parsed.protocol)) {
      return ''
    }
    return parsed.href
  } catch {
    return ''
  }
}

const commentLeadingLink = (item: Comment) => {
  if (!item.replyToAuthor) {
    return undefined
  }
  return {
    text: `@${item.replyToAuthor}`,
    href: `#comment-${item.replyToId || item.parentId}`
  }
}

</script>

<template>
  <ul class="comment-tree" :class="`level-${currentLevel}`">
    <li v-for="item in comments" :key="item.ID" class="comment-node">
      <div :id="`comment-${item.ID}`" class="comment-box card">
        <div class="avatar-wrap">
          <div class="comment-avatar-frame">
            <BlogImage class="avatar" :src="avatarUrl(item)" :alt="item.author || 'avatar'" />
            <BlogCommentVerifyIcon :identity="item.identity" />
          </div>
          <span class="floor-no">#{{ item.floor || item.ID }}</span>
        </div>
        <div class="main">
          <div class="head-row">
            <div class="comment-author">
              <a
                v-if="resolveAuthorUrl(item.url)"
                class="author-link"
                :href="resolveAuthorUrl(item.url)"
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                <strong>{{ item.author }}</strong>
              </a>
              <strong v-else>{{ item.author }}</strong>
              <BlogUiTooltip v-if="item.userId" content="此用户已通过 ID 验证">
                <span class="engine-user-verify-icon" role="img" aria-label="此用户已通过 ID 验证" />
              </BlogUiTooltip>
            </div>
            <span class="comment-time">
              <i class="fa-regular fa-clock" aria-hidden="true" />
              {{ formatTime(item.CreatedAt) }}
            </span>
          </div>
          <div v-if="item.identity?.authType" class="identity-row">
            <span class="identity-badge">{{ authLabel(item) }}</span>
            <span class="identity-info" v-if="item.identity.remark">
              <BlogUiTooltip :content="authLabel(item) + ': ' + item.identity.remark">
                {{ item.identity.remark }}
              </BlogUiTooltip>
            </span>
          </div>
          <div class="content">
            <BlogMarkdownContent
              :source="item.content"
              :leading-link="commentLeadingLink(item)"
              decode-markdown-markers
            />
          </div>
          <div v-if="item.ipRegion || parseUa(item).show || canReply(item)" class="comment-footer">
            <p v-if="item.ipRegion || parseUa(item).show" class="ua">
              <template v-if="item.ipRegion">
                <i class="fa-solid fa-location-dot" aria-hidden="true" />
                <span>{{ item.ipRegion }}</span>
              </template>
              <template v-if="parseUa(item).show">
                <i :class="parseUa(item).systemIcon" aria-hidden="true" />
                <span>{{ parseUa(item).system }}</span>
                <i :class="parseUa(item).browserIcon" aria-hidden="true" />
                <span>{{ parseUa(item).browser }}</span>
              </template>
            </p>
            <button v-if="canReply(item)" class="reply-btn" type="button" @click="emit('reply', item)">
              <i class="fa-solid fa-reply" aria-hidden="true" />
              <span>回复</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="canShowReplyForm(item)" :id="`reply-form-${item.ID}`" class="reply-inline">
        <slot name="reply-form" :item="item" />
      </div>

      <Transition name="reply-collapse">
        <div v-if="item.children?.length && currentLevel < 1" v-show="!isCollapsed(item.ID)" class="children-wrap">
          <BlogCommentTree
            :comments="item.children"
            :gravatar-mirror="gravatarMirror"
            :replying-id="replyingId"
            :replying-parent-id="replyingParentId"
            :level="currentLevel + 1"
            :allow-reply="allowReply"
            @reply="emit('reply', $event)"
          >
            <template #reply-form="slotProps">
              <slot name="reply-form" v-bind="slotProps" />
            </template>
          </BlogCommentTree>
        </div>
      </Transition>
      <button v-if="item.children?.length && currentLevel < 1" class="collapse-btn" type="button" @click="toggleChildren(item.ID)">
        {{ isCollapsed(item.ID) ? `展开回复(${item.children.length})` : `折叠回复(${item.children.length})` }}
      </button>
    </li>
  </ul>
</template>

<style scoped>
.comment-tree {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.comment-node {
  display: grid;
  gap: 12px;
}

.comment-tree.level-1 {
  position: relative;
  margin-left: 40px;
}

.comment-tree.level-1::before {
  content: '';
  position: absolute;
  left: -20px;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background: var(--line);
}

.comment-tree.level-1 .comment-node {
  position: relative;
}

.comment-tree.level-1 .comment-node::before {
  content: '';
  position: absolute;
  left: -20px;
  top: 30px;
  width: 12px;
  height: 1px;
  background: var(--line);
}

.comment-box {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  padding: 14px 16px;
  scroll-margin-top: var(--anchor-offset);
}

.avatar {
  width: 40px;
  height: 40px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--bg-soft);
  object-fit: cover;
}

.comment-avatar-frame {
  position: relative;
  width: 40px;
  height: 40px;
}

.avatar-wrap {
  display: grid;
  justify-items: center;
  gap: 6px;
}

.floor-no {
  font-size: 11px;
  color: var(--text-soft);
}

.main {
  min-width: 0;
}

.head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.head-row span {
  font-size: 12px;
  color: var(--text-soft);
}

.comment-time {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.comment-author {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 4px;
}

.comment-author :deep(.ui-tooltip-wrap) {
  display: inline-flex;
  flex: 0 0 18px;
  align-self: center;
  overflow: visible;
}

.engine-user-verify-icon {
  display: block;
  width: 18px;
  height: 18px;
  background: url('~/assets/id-verify.svg') center / contain no-repeat;
}

.author-link {
  color: var(--brand);
  text-decoration: none;
}

.author-link:hover {
  color: var(--brand);
}

.identity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: var(--text-soft);
}

.identity-info {
  max-width: 70%;
  white-space: nowrap;
}

.identity-badge {
  padding: 2px 8px;
  border-radius: 999px;
  color: var(--brand);
  background: var(--brand-soft);
  white-space: nowrap;
}

.content {
  margin-bottom: 6px;
  line-height: 1.7;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.content :deep(.md-leading-link) {
  text-decoration: none;
}

.content :deep(.md-leading-link:hover) {
  text-decoration: none;
}

.comment-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
}

.ua {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  color: var(--text-soft);
}

.ua i {
  width: 14px;
  text-align: center;
}

.reply-btn {
  margin-left: auto;
  border: 0;
  padding: 0;
  color: var(--brand);
  cursor: pointer;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.reply-btn i {
  font-size: 11px;
}

.reply-inline {
  margin-left: 0;
  width: 100%;
  max-width: 100%;
}

.reply-inline :deep(.comment-form) {
  width: 100%;
}

.children-wrap {
  display: grid;
  gap: 10px;
  overflow: visible;
}

.reply-collapse-enter-active,
.reply-collapse-leave-active {
  transition:
    max-height 0.22s ease,
    opacity 0.18s ease,
    transform 0.18s ease;
  overflow: hidden;
}

.reply-collapse-enter-from,
.reply-collapse-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-2px);
}

.reply-collapse-enter-to,
.reply-collapse-leave-from {
  max-height: 1200px;
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reply-collapse-enter-active,
  .reply-collapse-leave-active {
    transition: none;
  }
}

.collapse-btn {
  margin-left: 0;
  justify-self: flex-start;
  border: 0;
  padding: 0;
  font-size: 12px;
  color: var(--text-soft);
  cursor: pointer;
  background: transparent;
}

@media (max-width: 760px) {
  .comment-box {
    overflow: hidden;
  }

  .comment-tree.level-1 {
    margin-left: 30px;
  }

  .comment-tree.level-1::before {
    left: -15px;
  }

  .comment-tree.level-1 .comment-node::before {
    left: -15px;
    width: 10px;
  }

  .collapse-btn {
    margin-left: 0;
  }

  .reply-inline {
    margin-left: 0;
    width: 100%;
    max-width: 100%;
  }
}
</style>
